import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, resolveImage, hexToRgba } from '../utils';
import SectionDivider from './SectionDivider';

export default function SpotlightSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'left';

    const title = content.title || '';
    const subtitle = content.subtitle || '';
    const description = content.description || '';
    const imageUrl = resolveImage(content, 'image', mediaFolders) || content.image_url || '';
    const stats = content.stats || [];

    const imageFirst = variant === 'left';

    return (
        <section className={`${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <div className={`grid md:grid-cols-2 gap-12 lg:gap-16 items-center ${imageFirst ? '' : 'md:[direction:rtl]'}`}>
                    {/* Image side */}
                    <div className={`${imageFirst ? '' : 'md:[direction:ltr]'}`}>
                        {imageUrl ? (
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3]">
                                <img src={imageUrl} alt={title} className="w-full h-full object-cover" loading="lazy" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                            </div>
                        ) : (
                            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] flex items-center justify-center"
                                style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : hexToRgba(s.accent, 0.03) }}>
                                <svg className="w-16 h-16 opacity-20" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: s.accent }}>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.41a2.25 2.25 0 013.182 0l2.909 2.91m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                                </svg>
                            </div>
                        )}
                    </div>

                    {/* Text side */}
                    <div className={`${imageFirst ? '' : 'md:[direction:ltr]'}`}>
                        {subtitle && <span className="text-sm font-semibold tracking-wider uppercase" style={{ color: s.accent }}>{subtitle}</span>}
                        {title && <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-3" style={{ color: s.text }}>{title}</h2>}
                        {description && <p className="mt-5 text-lg leading-relaxed" style={{ color: s.textSub }}>{description}</p>}

                        {stats.length > 0 && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 mt-10 pt-8 border-t" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' }}>
                                {stats.map((stat, i) => (
                                    <div key={i} className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                                        style={{ transitionDelay: visible ? `${i * 150}ms` : '0ms' }}>
                                        <p className="text-3xl font-extrabold" style={{ color: s.accent }}>{stat.value}</p>
                                        <p className="text-sm mt-1" style={{ color: s.textSub }}>{stat.label}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color="transparent" position="bottom"  config={config}/>
        </section>
    );
}
